<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button class="btn btn-primary" @click="handleCreate">
                <i class="ri-add-line"></i>
                新增 QR Code
              </button>
      </div>
    </div>

    <div class="page-container">
    <div class="line-qrcode-intro" :class="{ 'is-expanded': introOpen }">
      <button type="button" class="intro-toggle" @click="introOpen = !introOpen">
        <span class="intro-title">
          <i class="ri-booklet-line"></i>
          功能說明
        </span>
        <span class="intro-toggle-action">
          <span class="intro-toggle-label">{{ introOpen ? '收起' : '查看說明' }}</span>
          <i :class="introOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
        </span>
      </button>
      <div v-show="introOpen" class="intro-body">
        <p>
          <strong>加好友 QR Code</strong>用於實體文宣、櫃台、活動背板等場景：顧客以手機掃描後，會經由系統產生的連結進入
          <strong>LIFF</strong> 流程，必要時引導加入您的 LINE 官方帳號好友；每組 QR Code 帶有獨立代碼，便於區分來源與成效。
        </p>
        <ul>
          <li>列表中的<strong>掃描次數</strong>、<strong>加好友次數</strong>、<strong>綁定次數</strong>可觀察各 QR 的引流與會員綁定狀況；點「詳情」可下載圖檔、複製連結與查看完整資訊。</li>
          <li>新增或編輯時可設定所屬 LINE 頻道、描述、有效期限、<strong>自動貼標</strong>與 QR 中央圖示等，方便區分用途並做後續分眾分析。</li>
        </ul>
      </div>
    </div>

    <div class="page-toolbar">
      <div class="search-box">
        <input
          v-model="searchKeyword"
          type="text"
          class="form-control"
          placeholder="搜尋 QR Code 名稱或代碼..."
          @input="handleSearch"
        />
        <i class="ri-search-line"></i>
      </div>
    </div>

    <div v-if="configWarnings.length > 0" class="config-warning">
      <i class="ri-error-warning-line"></i>
      <div>
        <p v-for="(msg, i) in configWarnings" :key="i">{{ msg }}</p>
      </div>
      <a href="/client/line/settings" class="btn btn-sm btn-warning">前往 LINE 頻道設定</a>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="loadQrcodes">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>QR Code 代碼</th>
            <th>設定名稱</th>
            <th>掃描次數</th>
            <th>加好友次數</th>
            <th>綁定次數</th>
            <th>狀態</th>
            <th>建立時間</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="qrcodes.length === 0">
            <td colspan="8" class="empty-state">
              沒有找到 QR Code
            </td>
          </tr>
          <tr v-for="qrcode in qrcodes" :key="qrcode.id">
            <td>
              <code>{{ qrcode.qrcodeCode }}</code>
            </td>
            <td>{{ qrcode.configName || '-' }}</td>
            <td>{{ qrcode.scanCount || 0 }}</td>
            <td>{{ qrcode.addFriendCount || 0 }}</td>
            <td>{{ qrcode.bindCount || 0 }}</td>
            <td>
              <span :class="getStatusBadgeClass(qrcode.isActive)">
                {{ qrcode.isActive ? '啟用' : '停用' }}
              </span>
            </td>
            <td>{{ formatDate(qrcode.createdAt) }}</td>
            <td>
              <div class="action-buttons">
                <button
                  class="btn btn-sm btn-secondary"
                  @click="handleView(qrcode.id)"
                  title="查看詳情"
                >
                  <i class="ri-eye-line"></i>
                </button>
                <button
                  class="btn btn-sm btn-secondary"
                  @click="handleDownload(qrcode.id, qrcode.qrcodeCode)"
                  title="下載 QR Code"
                >
                  <i class="ri-download-line"></i>
                </button>
                <button
                  class="btn btn-sm btn-secondary"
                  @click="handleEdit(qrcode.id)"
                  title="編輯"
                >
                  <i class="ri-edit-line"></i>
                </button>
                <button
                  class="btn btn-sm btn-danger"
                  @click="handleDelete(qrcode.id)"
                  title="刪除"
                >
                  <i class="ri-delete-bin-line"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ListPagination
      v-if="pagination.total > 0"
      :page="pagination.page"
      :total-pages="pagination.totalPages"
      :total="pagination.total"
      @change="handlePageChange"
    />
    </div>
  </div>
</template>

<script>
import { usePageTitle } from '@/composables/usePageTitle.js';
import lineQrcodeApi from '@/api/modules/lineQrcode.js';
import lineConfigApi from '@/api/modules/lineConfig.js';

export default {
  name: 'LineQrcodeList',
  setup() {
    const { pageTitle } = usePageTitle('加好友QR Code');
    return { pageTitle };
  },
  data() {
    return {
      introOpen: false,
      loading: false,
      error: null,
      searchKeyword: '',
      qrcodes: [],
      configWarnings: [],
      pagination: {
        page: 1,
        pageSize: 20,
        total: 0,
        totalPages: 0
      },
      searchTimeout: null
    };
  },
  mounted() {
    this.loadQrcodes();
    this.checkLineConfigs();
  },
  methods: {
    async checkLineConfigs() {
      try {
        const response = await lineConfigApi.list({ pageSize: 100 });
        const configs = response?.data?.items || response?.data || [];
        if (configs.length === 0) {
          this.configWarnings = ['尚未設定任何 LINE 頻道，請先至「LINE 頻道設定」新增頻道後再建立 QR Code。'];
          return;
        }
        const warnings = [];
        configs.forEach(c => {
          if (!c.messagingApi?.botBasicId) {
            warnings.push(`LINE 頻道「${c.configName}」未設定 Bot Basic ID，掃描 QR Code 後將無法自動導向官方帳號聊天室。`);
          }
        });
        this.configWarnings = warnings;
      } catch (e) {
        console.warn('檢查 LINE 頻道設定失敗:', e.message);
      }
    },

    async loadQrcodes() {
      this.loading = true;
      this.error = null;

      try {
        const params = {
          page: this.pagination.page,
          pageSize: this.pagination.pageSize
        };

        if (this.searchKeyword) {
          params.search = this.searchKeyword;
        }

        const response = await lineQrcodeApi.list(params);
        
        if (response && response.success && response.data) {
          this.qrcodes = response.data.items || response.data || [];
          const pag = response.data?.pagination;
          if (pag) {
            const total = pag.totalItems ?? pag.total ?? 0;
            this.pagination = {
              ...this.pagination,
              page: pag.page ?? this.pagination.page,
              total,
              totalPages: pag.totalPages || Math.ceil(total / this.pagination.pageSize) || 1
            };
          } else if (response.data && Array.isArray(response.data)) {
            // 如果沒有分頁資訊，嘗試從 data 推斷
            this.pagination.total = response.data.length;
            this.pagination.totalPages = Math.ceil(response.data.length / this.pagination.pageSize);
          }
        } else {
          const errorMsg = (response && response.message) || '獲取 QR Code 列表失敗';
          this.error = errorMsg;
        }
      } catch (err) {
        console.error('載入 QR Code 列表失敗:', err);
        this.error = err.message || err.data?.message || '獲取 QR Code 列表失敗';
      } finally {
        this.loading = false;
      }
    },
    
    handleSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.pagination.page = 1;
        this.loadQrcodes();
      }, 500);
    },
    
    handlePageChange(page) {
      if (page >= 1 && page <= this.pagination.totalPages) {
        this.pagination.page = page;
        this.loadQrcodes();
      }
    },
    
    handleCreate() {
      this.$router.push({ name: 'ClientCreateLineQrcode' });
    },
    
    handleView(id) {
      this.$router.push({ name: 'ClientLineQrcodeDetail', params: { id } });
    },
    
    handleEdit(id) {
      this.$router.push({ name: 'ClientEditLineQrcode', params: { id } });
    },
    
    async handleDownload(id, qrcodeCode) {
      try {
        // 顯示載入提示
        this.$message?.info('正在下載 QR Code...');
        
        // 調用 API 獲取 QR Code 圖片
        const response = await lineQrcodeApi.getImage(id);
        
        if (response && response.success && response.data) {
          const imageUrl = response.data.imageUrl || response.data.image;
          
          if (!imageUrl) {
            this.$message?.error('無法獲取 QR Code 圖片');
            return;
          }
          
          // 創建下載連結
          const link = document.createElement('a');
          link.href = imageUrl;
          link.download = `qrcode_${qrcodeCode || id}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          this.$message?.success('QR Code 下載成功');
        } else {
          this.$message?.error('下載失敗：無法獲取 QR Code 圖片');
        }
      } catch (error) {
        console.error('下載 QR Code 失敗:', error);
        const errorMessage = error?.response?.data?.message || error?.message || '下載 QR Code 失敗';
        this.$message?.error(errorMessage);
      }
    },
    
    async handleDelete(id) {
      if (!confirm('確定要刪除此 QR Code？')) {
        return;
      }

      const originalLoading = this.loading;
      this.loading = true;
      this.error = null;

      try {
        const response = await lineQrcodeApi.delete(id);
        
        if (response && response.success) {
          this.$message?.success('刪除成功');
          
          if (this.qrcodes.length === 1 && this.pagination.page > 1) {
            this.pagination.page = this.pagination.page - 1;
          }
          
          await this.loadQrcodes();
        } else {
          const errorMsg = (response && response.message) || '刪除 QR Code 失敗';
          this.$message?.error(errorMsg);
        }
      } catch (err) {
        console.error('刪除 QR Code 失敗:', err);
        const errorMsg = err.message || err.data?.message || '刪除 QR Code 失敗，請稍後再試';
        this.$message?.error(errorMsg);
      } finally {
        this.loading = originalLoading;
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    
    getStatusBadgeClass(isActive) {
      return isActive ? 'badge badge-success' : 'badge badge-secondary';
    }
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_table-common.scss';
@import '~@/assets/scss/components/_list-page-common.scss';

.line-qrcode-intro {
  background: #e8f4ff;
  border: 1px solid #b3d7ff;
  border-left: 4px solid #1890ff;
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 16px;
  font-size: 13px;
  color: #1a3a52;
  line-height: 1.65;
  min-width: 0;
}

.line-qrcode-intro.is-expanded {
  padding: 14px 16px;
}

.line-qrcode-intro .intro-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.line-qrcode-intro .intro-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #0d3d66;
  display: flex;
  align-items: center;
  gap: 8px;
}

.line-qrcode-intro .intro-title i {
  font-size: 20px;
  color: #1890ff;
}

.line-qrcode-intro .intro-toggle-action {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  font-size: 13px;
  color: #0d3d66;
}

.line-qrcode-intro .intro-body {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(24, 144, 255, 0.2);
}

.line-qrcode-intro .intro-body p {
  margin: 0 0 10px 0;
}

.line-qrcode-intro .intro-body ul {
  margin: 0;
  padding-left: 18px;
}

.line-qrcode-intro .intro-body li {
  margin-bottom: 6px;
}

.line-qrcode-intro .intro-body li:last-child {
  margin-bottom: 0;
}

.table-wrapper table {
  min-width: 1200px;
}

.config-warning {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #fff8e1;
  border: 1px solid #ffc107;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 20px;
  color: #7a5000;
  font-size: 14px;

  i {
    font-size: 20px;
    color: #ffc107;
    flex-shrink: 0;
    margin-top: 2px;
  }

  div {
    flex: 1;

    p {
      margin: 0 0 4px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}
</style>
